import axios from 'axios';
import axiosRetry from 'axios-retry';
import { routes, apiRoutes } from '$src/routes';
import { uuid } from '$src/utils/uuid';
import { setupCache } from 'axios-cache-interceptor';
import { setCSRFHeader } from '$src/utils/csrf';
import getEnvironment, { isLocalEnv } from '$src/utils/enviroment';
import { controlCenterApiUrl, localUrls } from '$src/constants/urls';
import { HTTP_STATUS_CODE_UNAUTHORIZED, AXIOS_MAX_RETRIES, CSRF_HEADER, HTTP_STATUS_CODE_TOO_EARLY } from './constants';

const destructure = (response) => response.data;

let csrfToken = '';

// Control Center API HOF
function StarkAPI() {
  const requestHandler = (request) => {
    const requestId = `cc-${uuid()}`;
    request.headers['Content-Type'] = 'application/json';
    request.headers['X-Amzn-Trace-Id'] = requestId;
    request.headers['X-Amzn-RequestId'] = requestId;
    if (csrfToken) setCSRFHeader(request, csrfToken);
    return request;
  };
  const errorHandler = (err) => {
    if (err.response?.status === HTTP_STATUS_CODE_UNAUTHORIZED) {
      window.location.assign(routes.root);
      return null;
    }
    return Promise.reject(err);
  };
  const successHandler = (response) => response;

  const baseURL = isLocalEnv() ? `${localUrls[getEnvironment()]}:3000` : controlCenterApiUrl[getEnvironment()];
  const instance = axios.create({ baseURL });
  const axiosInstance = setupCache(instance);
  axiosInstance.interceptors.request.use(requestHandler);
  axiosInstance.interceptors.response.use(successHandler, errorHandler);

  const axiosVerifyInstance = axios.create({ baseURL });
  axiosVerifyInstance.interceptors.request.use(requestHandler);

  const axiosRetryInstance = axios.create({ baseURL });
  axiosRetryInstance.interceptors.request.use(requestHandler);
  axiosRetryInstance.interceptors.response.use(successHandler);
  axiosRetry(axiosRetryInstance, {
    retryDelay: axiosRetry.exponentialDelay,
    retries: AXIOS_MAX_RETRIES,
    retryCondition: (error) => error.response.status === HTTP_STATUS_CODE_TOO_EARLY,
  });

  return {
    getAccessToken() {
      return axiosVerifyInstance.get(apiRoutes.session, {}, { withCredentials: true }).then((response) => {
        csrfToken = response.headers[CSRF_HEADER];
        return {
          ...response?.data,
          csrfToken,
        };
      });
    },
    fetchFeatures(principalDomain, principal) {
      return axiosInstance.get(apiRoutes.features(principalDomain, principal)).then(destructure);
    },
    fetchLocations(userId) {
      return axiosInstance.get(apiRoutes.allLocations(userId)).then(destructure);
    },
    fetchSubscription(userId, locationId) {
      return axiosInstance.get(apiRoutes.subscription(userId, locationId)).then(destructure);
    },
    fetchAllDevices(userId, locationId) {
      return axiosInstance.get(apiRoutes.allDevices(userId, locationId), { id: `fetchAllDevices-${userId}-${locationId}` }).then(destructure);
    },
    subscribeDevice(device, userId, locationId) {
      return axiosInstance
        .post(
          apiRoutes.subscribe(userId),
          { ...device, userId },
          {
            cache: {
              update: {
                [`fetchAllDevices-${userId}-${locationId}`]: (devicesCache) => {
                  if (devicesCache.state !== 'cached') {
                    return 'ignore';
                  }

                  devicesCache.data.data.find((d) => d.doorbotId === device.doorbotId).starkEnrolled = true;

                  return devicesCache;
                },
              },
            },
          },
        )
        .then(destructure);
    },
    unsubscribeDevice(userId, doorbotId, locationId) {
      return axiosInstance
        .delete(apiRoutes.unsubscribe(userId, doorbotId), {
          cache: {
            update: {
              [`fetchAllDevices-${userId}-${locationId}`]: (devicesCache) => {
                if (devicesCache.state !== 'cached') {
                  return 'ignore';
                }

                devicesCache.data.data.find((d) => d.doorbotId === doorbotId).starkEnrolled = false;

                return devicesCache;
              },
            },
          },
        })
        .then(destructure);
    },
    fetchPreferences(userId, locationId) {
      return axiosInstance.get(apiRoutes.preferences(userId, locationId), { id: `fetchPreferences-${userId}-${locationId}` }).then(destructure);
    },
    fetchContacts(userId, locationId) {
      return axiosInstance.get(apiRoutes.contacts(userId, locationId)).then(destructure);
    },
    updatePreferences(data, userId, locationId) {
      return axiosInstance
        .put(apiRoutes.preferences(userId, locationId), data, {
          cache: {
            update: {
              [`fetchPreferences-${userId}-${locationId}`]: (preferencesCache, updatedPreferences) => {
                if (preferencesCache.state !== 'cached') {
                  return 'ignore';
                }

                preferencesCache.data.data = JSON.parse(updatedPreferences.config.data);

                return preferencesCache;
              },
            },
          },
        })
        .then(destructure);
    },
    sendStats(userId, stats) {
      return axiosInstance.post(apiRoutes.sendStats(userId), stats).then(destructure);
    },
    fetchMonitoringStatus(userId, locationId) {
      return axiosInstance.get(apiRoutes.monitoringStatus(userId, locationId), { cache: false }).then(destructure);
    },
    setOnboardingCompleted(userId, locationId) {
      return axiosInstance.post(apiRoutes.onboardingComplete(userId, locationId)).then(destructure);
    },
    fetchVSGModeStatus(userId, locationId) {
      return axiosInstance.get(apiRoutes.status(userId, locationId), { cache: false }).then(destructure);
    },
    createEntitlement(userId, locationId) {
      return axiosInstance.post(apiRoutes.entitlement(userId, locationId));
    },
    cancelFreeTrial(userId, locationId) {
      return axiosInstance.delete(apiRoutes.entitlement(userId, locationId));
    },
    fetchEvents(userId, locationId, startTime, limit, nextToken, resolutionTypes) {
      return axiosInstance.get(apiRoutes.events(userId, locationId, startTime, limit, nextToken, resolutionTypes)).then(destructure);
    },
    fetchEventSummary(userId, locationId) {
      return axiosInstance.get(apiRoutes.eventSummary(userId, locationId)).then(destructure);
    },
    fetchEventsTimeseries(userId, locationId, resolutionTypes) {
      return axiosInstance.get(apiRoutes.eventsTimeseries(userId, locationId, resolutionTypes)).then(destructure);
    },
    completeOnboarding(userId, locationId) {
      return axiosRetryInstance.post(apiRoutes.completeOnboarding(userId, locationId)).then(destructure);
    },
    endAlarmPractice(userId, locationId) {
      return axiosInstance.post(apiRoutes.endAlarmPractice(userId, locationId)).then(destructure);
    },
    fetchVSGState(userId, locationId) {
      return axiosInstance.get(apiRoutes.state(userId, locationId), { id: `fetchVSGState-${userId}-${locationId}` }).then(destructure);
    },
    changeVSGState(userId, locationId, paused) {
      return axiosInstance
        .post(
          apiRoutes.state(userId, locationId),
          { pauseEnable: paused },
          {
            cache: {
              update: {
                [`fetchVSGState-${userId}-${locationId}`]: (VSGStateCache) => {
                  if (VSGStateCache.state !== 'cached') {
                    return 'ignore';
                  }

                  VSGStateCache.data.data.paused = !VSGStateCache.data.data.paused;

                  return VSGStateCache;
                },
              },
            },
          },
        )
        .then(destructure);
    },
    fetchLocationsStatus(userId) {
      return axiosInstance.get(apiRoutes.allLocationsStatus(userId)).then(destructure);
    },
    fetchGuard(userId, locationId) {
      return axiosInstance.get(apiRoutes.guard(userId, locationId), { cache: false }).then(destructure);
    },
    createGuard(userId, locationId, guard) {
      return axiosInstance.put(apiRoutes.guard(userId, locationId), guard).then(destructure);
    },
    deleteGuard(userId, locationId) {
      return axiosInstance.delete(apiRoutes.guard(userId, locationId)).then(destructure);
    },
  };
}

export const controlCenterApi = StarkAPI();
